import { useState } from 'react'
import type { Schemas } from '@npc/api-client'

import { Card } from '../../components/card'
import { cn } from '../../components/cn'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { useInvitations, useProjects, useRevokeInvitation, useTeam, useTeamMe } from '../../app/queries'
import { shortDate } from '../../app/format'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { InviteDialog } from './invite-dialog'
import { MemberDialog } from './member-dialog'
import './team.css'

/**
 * 屏 `team` 一起管。
 *
 * ## 这一屏最要紧的一件事：范围不是权限
 *
 * 中间那一档（「干活的」）是这个产品真正的权限设计——**外包能干活，
 * 但拿不到原始密钥**。`services/control/internal/team` 的包注释说得最准：
 *
 * > 「拿不到」不是界面上不给按钮，是这一档根本没有 `CapReadRawSecret` 这项能力。
 *
 * 落到界面上就是两条：
 *
 * **一、「能碰什么」那一列说的是范围，不是权限。** 一个 `global` 为真的「干活的」
 * 看得见所有项目，只是拿不到钥匙。**所以这一列里不许有任何东西被灰掉**——
 * 灰掉会读成「他没权限看这个项目」，那是错的，而且错得刚好反过来。
 *
 * **二、能干什么由 `capabilities` 那份机器码说了算。** 契约原话：
 * 「前端照着它画按钮，不要自己写一份『operator 能不能删机器』的表——
 * 权限规则有两份的那天，UI 上能点的和后端认的就对不上了」。
 * 所以这一屏没有一处 `role === 'owner'` 的判断。
 *
 * ## 身份的中文名只有一个来源
 *
 * `MemberRole` 是机器码。名册那一列要显示中文，而 `GET /team` 的 `roles`
 * 里每一档都带着后端渲染好的 `name`——**从那儿查**，不在前端拿
 * `team.role.owner` 再渲染一遍。契约两处都写了同一句担心：
 * 同一个角色在两屏上迟早出现两种说法。
 */
export function TeamScreen() {
  const team = useTeam()
  const projects = useProjects()
  const me = useTeamMe()
  const invitations = useInvitations()
  const revoke = useRevokeInvitation()
  const [inviting, setInviting] = useState(false)
  const [editing, setEditing] = useState<Schemas['Member'] | null>(null)
  // 能不能叫人进来看 capabilities，不看 role（契约原话：前端照着它画按钮）。读不到就当不能。
  const canManage = (me.data?.capabilities ?? []).includes('manage_team')

  if (team.isPending) return <ScreenLoading shape="list" width="reading" />
  if (team.error) return <ScreenError error={team.error} />

  const members = team.data?.items ?? []
  const roles = team.data?.roles ?? []
  const allProjects = projects.data?.items ?? []

  const roleName = new Map(roles.map((role) => [role.role, role.name]))
  const projectName = new Map(allProjects.map((project) => [project.id, project.name]))

  return (
    <Page width="reading" className="team-theme">
      {/* Keep live subtitle even if member count mismatches solo line; do not invent count-gated copy. */}
      <PageHeader
        title={<T id="team.page.title" />}
        subtitle={<T id="team.page.subtitle" />}
        actions={
          <button
            type="button"
            disabled={!canManage}
            title={canManage ? undefined : t('team.invite.no_cap')}
            data-testid="team-invite"
            onClick={() => setInviting(true)}
            className={cn(
              'h-[32px] rounded-npc-button border-none px-[14px] font-[inherit] text-[13px] font-medium',  /* npc-token-allow: wave2 pill CTA height from comps */
              canManage
                ? 'bg-npc-green text-npc-panel'
                : 'cursor-not-allowed bg-npc-surface text-npc-ink-softer',
            )}
          >
            <T id="team.invite.label" />
          </button>
        }
        className="mb-npc-modal"
      />

      <Card padding="none" className="mb-npc-card-lg overflow-hidden">
        <div className={cn(COLUMNS, 'grid border-b border-npc-border-soft px-npc-card py-npc-grid-sm text-npc-caption-sm text-npc-ink-soft')}>
          <span>
            <T id="team.table.person" />
          </span>
          <span>
            <T id="team.table.role" />
          </span>
          <span>
            <T id="team.table.scope" />
          </span>
        </div>

        {members.length === 0 ? (
          <p className="m-0 px-npc-card py-npc-card-sm text-npc-body-sm text-npc-ink-soft">
            <T id="team.empty" />
          </p>
        ) : (
          members.map((member) => (
            <Row
              key={member.user_id}
              member={member}
              roleName={roleName.get(member.role)}
              projectName={projectName}
              allProjects={allProjects}
              canManage={canManage}
              onEdit={() => setEditing(member)}
            />
          ))
        )}

      </Card>

      {/* 还挂着的邀请：只有能管团队的人看得到；一条都没有就整块不画（沉默是好消息）。 */}
      {canManage && (invitations.data?.items ?? []).length > 0 ? (
        <Card padding="none" className="mb-npc-card-lg overflow-hidden" data-testid="team-invitations">
          <div className="border-b border-npc-border-soft px-npc-card py-npc-grid-sm">
            <div className="text-npc-section-sm font-semibold">
              <T id="team.invites.heading" />
            </div>
            <div className="mt-[2px] text-npc-caption text-npc-ink-soft">
              <T id="team.invites.hint" />
            </div>
          </div>
          {(invitations.data?.items ?? []).map((inv) => (
            <div
              key={inv.id}
              data-testid="team-invitation"
              className={cn(COLUMNS, 'grid items-center border-b border-npc-border-softer px-npc-card py-npc-grid-sm')}
            >
              <div className="min-w-0">
                <div className="text-npc-body font-medium">{inv.email}</div>
                <div className="text-npc-meta text-npc-ink-softer">
                  {t('team.invites.expires', { date: shortDate(inv.expires_at) })}
                </div>
              </div>
              <span className="text-npc-body-sm">{inv.role_name}</span>
              {/* 范围不是权限：项目列表为空 = 全局可见（老板那一档），照样把所有项目列出来，一处都不灰。
                  这里不按 role 判——那是契约点名不许的两份权限表。 */}
              <span className="text-npc-caption text-npc-ink-soft">
                {inv.projects.length === 0
                  ? allProjects.map((project) => project.name).join('、')
                  : inv.projects.map((id) => projectName.get(id) ?? id).join('、')}
              </span>
              <button
                type="button"
                data-testid="team-invitation-revoke"
                disabled={revoke.isPending}
                onClick={() => void revoke.mutateAsync(inv.id).catch(() => undefined)}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[4px] text-npc-caption text-npc-ink-medium disabled:opacity-50"
              >
                <T id="team.invites.revoke" />
              </button>
            </div>
          ))}
        </Card>
      ) : null}

      <InviteDialog open={inviting} roles={roles} projects={allProjects} onClose={() => setInviting(false)} />
      {/* 条件挂载而不是常挂传 open：表里的预填值取自 member 的 useState 初值，
          常挂的组件在 member 还是 null 时就把初值定死了。key 让换人时重建。 */}
      {editing ? (
        <MemberDialog
          key={editing.user_id}
          open
          member={editing}
          roles={roles}
          projects={allProjects}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Card>
        <div className="text-npc-section-sm font-semibold">
          <T id="team.roles.heading" />
        </div>
        <div className="mb-npc-card-sm mt-[4px] text-npc-caption text-npc-ink-soft">
          <T id="team.roles.hint" />
        </div>

        <div className="grid grid-cols-3 gap-npc-grid-sm">
          {roles.map((role) => (
            <div key={role.role} className="rounded-npc-inset border border-npc-border-soft p-npc-gap-lg">
              <div className="mb-npc-gap-xs text-npc-section-sm font-semibold">{role.name}</div>
              <div className="text-npc-caption leading-[1.6] text-npc-ink-soft">{role.summary}</div>
            </div>
          ))}
        </div>
      </Card>
    </Page>
  )
}

/** 四列，表头与每一行共用。最后一列是「改」。 */
const COLUMNS = 'grid-cols-[1.6fr_1fr_1.4fr_auto]'

function Row({
  member,
  roleName,
  projectName,
  allProjects,
  canManage,
  onEdit,
}: {
  member: Schemas['Member']
  roleName?: string
  projectName: Map<string, string>
  allProjects: Schemas['Project'][]
  canManage: boolean
  onEdit: () => void
}) {
  /**
   * 「能碰什么」。
   *
   * `global` 为真时 `projects` 是空数组（契约里写明的），**不能就让这一格空着**——
   * 空格子读起来是「他什么都碰不到」，而真相刚好相反。
   * 所以那种情况下把所有项目列出来：这不是推断，`global` 的定义就是它。
   *
   * ⚠️ 更好的做法是给一个「全部项目」这样的词，但 `docs/copy` 里没有，
   * 已提给流 0。在它到之前，逐个列出来是**真话且不会被误读**的那一种。
   */
  const scope = member.global
    ? allProjects.map((project) => project.name)
    : (member.projects ?? []).map((id) => projectName.get(id)).filter(Boolean)

  return (
    <div
      className={cn(
        COLUMNS,
        'grid items-center border-b border-npc-border-softer px-npc-card py-npc-grid-sm',
      )}
    >
      <div className="flex items-center gap-npc-gap">
        <span
          aria-hidden="true"
          className="flex size-[30px] shrink-0 items-center justify-center rounded-npc-full bg-npc-surface text-npc-meta text-npc-ink-medium"
        >
          {initials(member.name ?? member.email)}
        </span>
        <div className="min-w-0">
          <div className="text-npc-body font-medium">{member.name ?? member.email}</div>
          <div className="text-npc-meta text-npc-ink-softer">{member.email}</div>
        </div>
      </div>

      <span className="text-npc-body-sm">{roleName}</span>

      {/* **这一列一处都不许灰。** 范围不是权限：一个「干活的」看得见所有项目，
          只是拿不到原始密钥。灰掉会读成「他没权限看这个项目」 */}
      <span className="text-npc-caption text-npc-ink-soft">{scope.join('、')}</span>

      {/* 这一格是「改」。能不能改不看这行是谁的 role，看**我自己**有没有
          `manage_team`（GET /team/me）——契约只许一份权限表。老板那一行
          也不灰掉自己：「至少留一个老板」是服务端的规矩，UI 抢先算一遍
          就成了第二份。 */}
      {canManage ? (
        <button
          type="button"
          data-testid="team-member-edit"
          onClick={onEdit}
          className="rounded-npc-button border border-npc-border-soft px-[10px] py-[4px] text-npc-caption text-npc-ink-medium"
        >
          <T id="team.member.edit" />
        </button>
      ) : (
        <span />
      )}
    </div>
  )
}

/** 头像里那两个字。名字取头一个字，邮箱取 @ 前面头两位。 */
function initials(label: string): string {
  const at = label.indexOf('@')
  return at > 0 ? label.slice(0, Math.min(2, at)) : label.slice(0, 1)
}
